import {ChangeDetectionStrategy, Component, Input, OnChanges, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {AuthService} from "../../_services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatStepper} from "@angular/material/stepper";
import {Community, Domain, QuestionAnswer} from '../../_types/communities';
import {OnboardingQuestionsComponent} from '../onboarding-questions/onboarding-questions.component';
import {debounceTime, filter, of, switchMap, take, tap} from "rxjs";
import {ThemeService} from "../../_services/theme.service";
import {CommunitiesService} from "../../_services/communities.service";
import {AuthUser, TwoFaResponse} from "../../_types/auth";
import {GenericResponse} from "../../_types/shared";
import {LocalStorageService} from "../../_services/local-storage.service";
import {catchError} from "rxjs/operators";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrl: './register.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RegisterComponent implements OnChanges {
    @Input() invite: any;
    @Input() community?: Community;
    @Input() email?: string;
    signUpForm: FormGroup;
    usernameTaken: boolean = false;
    loading: boolean = true;
    companyImg: string = localStorage.getItem('communityLogo') || 'assets/explainLogoPlaceholder.png';
    companyName: string = localStorage.getItem('communityName') || 'Explain'
    image: any;
    photoFile: any;
    maxFileSize: number = 10;
    showCropper: boolean = false;
    needLogin: boolean = false;
    loginPrompt?: string;
    userRequiresProfiling: boolean = true;
    imageChangedEvent: any = '';
    eligibilityAnswers: QuestionAnswer[] = []
    profileAnswers: QuestionAnswer[] = []
    loginForm: FormGroup;
    otpForm: FormGroup;
    loginData: AuthUser | undefined;
    view: 'Login' | 'Code' = 'Login'
    sentTo: TwoFaResponse | undefined;
    timer: number = 0;
    passwordType: string = 'password';
    domains: Domain[] = [];
    @ViewChild('stepper') private myStepper: MatStepper | undefined;
    @ViewChild('appOnboardingQuestionsComponent', {static: false}) private appOnboardingQuestionsComponent?: OnboardingQuestionsComponent;

    constructor(private readonly formBuilder: FormBuilder,
                private authService: AuthService,
                public readonly themeService: ThemeService,
                private readonly router: Router,
                private readonly snackBar: MatSnackBar,
                private readonly localStorageService: LocalStorageService,
                private readonly route: ActivatedRoute,
                private readonly communitiesService: CommunitiesService) {
        this.signUpForm = this.formBuilder.group({
            username: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9._-]+$')]],
            postcode: ['', [Validators.required, Validators.pattern('^\\w{2,4}$'), Validators.minLength(2)]],
            email: ['', [Validators.required, Validators.email]],
            confirmEmail: ['', [Validators.required, Validators.email]],
            password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(32)]],
            confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(32)]],
            termsConsent: [false],
            privacyConsent: [false],
            bio: [''],
        });
        this.loginForm = this.formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
            keep_logged_in: [false]
        });
        this.otpForm = this.formBuilder.group({
            code: ['', [Validators.required]],
        });


        this.signUpForm.controls['username'].valueChanges.pipe(
            debounceTime(300),
            filter(value => value.length > 2),
            tap(),
            switchMap(value => {
                return this.authService.checkUsername(value).pipe(
                    // Handle API errors gracefully without breaking the stream
                    catchError(e => {
                        this.openSnackBar(e, 'error');
                        return of({username: value, available: true});
                    })
                );
            }),
            takeUntilDestroyed()
        ).subscribe(r => {
            this.usernameTaken = !r.available
        });
    }

    ngOnChanges(): void {
        if (this.community?.hide_post_code) {
            this.signUpForm.get('postcode')?.setErrors(null);
            this.signUpForm.get('postcode')?.clearValidators();
            this.signUpForm.get('postcode')?.updateValueAndValidity();
        } else {
            this.signUpForm.get('postcode')?.clearValidators();
            this.signUpForm.get('postcode')?.addValidators([Validators.required, Validators.pattern('^\\w{2,4}$'), Validators.minLength(2)]);
            this.signUpForm.get('postcode')?.updateValueAndValidity();
        }
        this.communitiesService.getDomainWhitelist(this.community?.id).pipe(take(1)).subscribe({
            next: (response: GenericResponse<Domain[]>): void => {
                this.domains = response.data;
            },
            error: err => {
                this.openSnackBar(err, 'error');
            }
        })

        if (this.community) {
            this.companyImg = this.community.logo;
            this.companyName = this.community.name;
            this.loading = false;

        } else {
            this.loading = false;
        }
        if (this.invite) {
            this.signUpForm.patchValue({
                email: this.invite.email,
                confirmEmail: this.invite.email
            })
        }
        if (this.email) {
            // email is passed so is part of SSO registration
            this.signUpForm.get('password')?.setErrors(null);
            this.signUpForm.get('password')?.clearValidators();
            this.signUpForm.get('password')?.updateValueAndValidity();
            this.signUpForm.get('confirmPassword')?.setErrors(null);
            this.signUpForm.get('confirmPassword')?.clearValidators();
            this.signUpForm.get('confirmPassword')?.updateValueAndValidity();
            let user: AuthUser | null = this.localStorageService.getItem<AuthUser | null>('SSOUser', null)
            if (user?.user?.email) {
                this.setUserEmail(user.user.email)
                const username = this.signUpForm.controls['username'];
                if (username.pristine && !username.value) {
                    username.setValue(user.user.email.split('@')[0].replace(/[^a-zA-Z0-9._-]/g, ''));
                }
            }
            if (user && user.user?.profile_picture && typeof user.user?.profile_picture == 'string') {
                const profilePic: string = user.user.profile_picture;

                this.imageUrlToFile(profilePic).then((r): void => {
                    this.photoFile = r;
                    this.image = profilePic;
                })
            }
        }
    }

    async imageUrlToFile(imageUrl: string,): Promise<File> {
        const response: Response = await fetch(imageUrl);
        const blob: Blob = await response.blob();
        return new File([blob], 'profile_picture.png', {type: blob.type});
    }

    imageCropped(event: any): void {
        this.photoFile = new File([event.blob], "profile_picture.png");
        const reader: FileReader = new FileReader();
        reader.readAsDataURL(event.blob);
        const that: this = this;
        reader.onloadend = function (): void {
            const base64data: string | ArrayBuffer | null = reader.result;
            if (base64data) {
                that.image = base64data;
            }
        };
    }

    create(): void {
        this.loading = true;
        const formValue = this.signUpForm.getRawValue();

        let body: any = {
            "password": formValue.password,
            "username": formValue.username,
            "email": formValue.email,
            "bio": formValue.bio,
            "postcode": formValue.postcode,
        }
        let referrer: string | null = this.route.snapshot.queryParamMap.get('referrer')
        if (referrer) {
            body.referrer = referrer;
        }

        if (!this.invite?.invite_token) {
            if (this.community?.id) {
                body.community_id = this.community?.id;
            } else {
                this.openSnackBar('There has been an error setting your community please try again', 'error')
                return;
            }
        }
        let user: AuthUser | null = this.localStorageService.getItem<AuthUser | null>('SSOUser', null)
        if (this.email && user) {
            let body = this.signUpForm.value;
            delete body.password;
            delete body.confirmPassword;
            delete body.email;
            delete body.confirmEmail;

            this.authService.patchUser(this.clean(body), this.photoFile).pipe(take(1)).subscribe(patchResponse => {
                // Save registration form details
                user.user.username = body.username;
                user.user.bio = body.bio;
                this.authService.addUserToCommunity(this.community!.id, user.token).subscribe(r => {
                    this.authService.refreshUser().then(r => {
                        this.authService.completeSSOSetup().subscribe({
                            next: data => {
                                this.localStorageService.removeItem('SSOUser');
                                this.completeRegistration(patchResponse.id)
                            }, error: error => {
                                this.openSnackBar(error, 'error')
                                console.error(error)
                                if (error['username'] === 'A user with that username already exists') {
                                    this.openSnackBar('Your username matches an existing explain account please try a different username', 'error')
                                }
                            }
                        })
                    })
                }, error => {
                    this.openSnackBar(error, 'error')
                    this.loading = false;
                })
            }, e => {
                this.openSnackBar(e, 'error');
            })
        } else {
            this.authService.createUser(body, this.photoFile, this.invite?.invite_token)
                .subscribe(
                    (data: any): void => {
                        this.loading = false;
                        if (data.data.requires_login) {
                            this.requireLoginAfterRegistration(formValue.email);
                        } else {
                            this.authService.login({
                                email: formValue.email,
                                password: formValue.password
                            }).subscribe(
                                (data: any): void => {
                                    if (data.data.mfa_required) {
                                        this.loginData = data.data;
                                        this.request2fa();
                                        return;
                                    }
                                    this.authService.setUser(data.data);
                                    this.completeRegistration(data.data.id)
                                },
                                (error: any): void => {
                                    this.openSnackBar(error, 'error')
                                    this.loading = false;
                                });
                        }
                    },
                    (error: any): void => {
                        if (error['username'] === 'A user with that username already exists') {
                            this.openSnackBar('Your username matches an existing explain account please login to complete your registration to this community', 'error')
                            this.needLogin = true;
                        } else if (error === 'A user with that email address already exists') {
                            this.openSnackBar('Your email matches an existing explain account please login to complete your registration to this community', 'error')
                            this.needLogin = true;
                        } else {
                            this.openSnackBar(error, 'error')
                        }
                        this.loading = false;
                    });
        }
    }


    login(): void {
        this.loading = true;
        let body = this.loginForm.value;
        body.community_id = this.community?.id
        this.authService.login(body)
            .subscribe(
                (data: any): void => {
                    if (data.data.mfa_required) {
                        this.loginData = data.data;
                        this.request2fa();
                        this.loading = false;
                    } else {
                        this.addToCommunity(data.data)
                    }
                },
                (error: any): void => {
                    this.openSnackBar(error, 'error')
                    this.loading = false;
                });
    }

    finishLogin(): void {
        if (!this.loginData) {
            this.openSnackBar('No login token returned from the server please try again', 'error')
            this.loading = false;
            return;
        }
        this.loading = true;
        this.authService.verify2fa({code: this.otpForm.value.code}, this.loginData.token)
            .subscribe({
                next: data => {
                    if (data.verified && this.loginData) {
                        this.addToCommunity(this.loginData);
                    } else {
                        this.loading = false;
                        this.openSnackBar('No login data', 'error')
                    }
                }, error: error => {
                    this.loading = false;
                    this.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
    }

    addToCommunity(data: AuthUser): void {
        this.authService.addUserToCommunity(this.community!.id, data.token).subscribe(r => {
            if (r.data?.mfa_required) {
                this.loginData = data;
                this.request2fa();
                this.loading = false;
                return;
            }
            this.authService.setUser(data);
            this.completeRegistration(data.id)
        }, error => {
            this.openSnackBar(error, 'error')
            this.loading = false;
        })
    }

    private requireLoginAfterRegistration(email: string): void {
        this.authService.clearStore();
        this.loginForm.patchValue({email, password: ''});
        this.loginPrompt = 'For security reasons, please log in and complete two-factor authentication.';
        this.view = 'Login';
        this.needLogin = true;
        this.openSnackBar('Please log in to your new account', 'success');
    }

    request2fa(): void {
        if (!this.loginData?.token) {
            this.openSnackBar('No login token returned from the server please try again', 'error')
            this.loading = false;
            return;
        }
        this.needLogin = true;
        this.view = 'Code';
        this.authService.request2fa(this.loginData.token).subscribe({
            next: r => {
                this.sentTo = r;
                this.timer = 25;
                let timeleft: number = 25;
                let that: this = this;
                const downloadTimer: ReturnType<typeof setTimeout> = setInterval(function (): void {
                    if (timeleft <= 0) {
                        clearInterval(downloadTimer);
                    }
                    that.timer = timeleft;
                    timeleft -= 1;
                }, 1000);
            }, error: error => {
                this.openSnackBar(error, 'error')
                console.error(error)
                this.loading = false;
            }
        });
    }

    completeRegistration(userId: number): void {
        const handleProfile = (): Promise<void> => {
            if (this.profileAnswers.length > 0) {
                return this.sendProfileQuestions(userId).then((): void => {
                    this.postRegistrationRedirect();
                });
            } else {
                this.postRegistrationRedirect();
                return Promise.resolve();
            }
        };

        if (this.eligibilityAnswers.length > 0) {
            this.sendEligibility().then((): void => {
                handleProfile();
            });
        } else {
            handleProfile();
        }
    }

    sendEligibility() {
        return new Promise((resolve, reject): void => {
            this.authService.checkEligibility(this.community!.id, this.eligibilityAnswers).pipe(take(1)).subscribe(r => {
                if (this.community) {
                    this.communitiesService.setCurrentDashboardCommunity(this.community)
                }
                resolve(r)
            }, e => {
                reject(e)
                this.openSnackBar(e, 'error')
            })
        });
    }

    postRegistrationRedirect(): void {
        this.openSnackBar('Community Registration Complete', 'success')
        if (this.community && this.community.register_post_redirect) {
            this.router.navigate(['/feed', this.community.register_post_redirect]);
        }
        if (this.community?.home_page_path) {
            this.router.navigateByUrl(this.community.home_page_path);
        } else {
            this.router.navigate(['/dashboard']);
        }
    }

    sendProfileQuestions(userId: number) {
        return new Promise((resolve, reject): void => {
            let done: number = 0;
            for (let answer of this.profileAnswers) {
                (answer as any)['user'] = userId;
                if (answer.options && answer.options[0] == null) {
                    answer.options = [];
                }
                this.authService.postProfileQuestionAnswer(answer).pipe(take(1)).subscribe(r => {
                    done++
                    if (done == this.profileAnswers.length) {
                        resolve(r)
                    }
                }, e => {
                    reject(e)
                    this.openSnackBar(e, 'error')
                })
            }
        });
    }

    onFileInput(event: any): void {
        (event.target as HTMLInputElement).value = '';
        if (!event.target.files[0]) {
            this.openSnackBar("Error Selecting Image.", 'error');
            return;
        }
        this.photoFile = event.target.files[0]

        var mimeType = this.photoFile?.type;
        if (mimeType?.match(/image\/*/) == null || !mimeType) {
            // check if image
            this.openSnackBar("Only images are supported.", 'error')
            return;
        }

        let filesize: number = parseFloat(((this.photoFile.size / 1024) / 1024).toFixed(1));

        if (filesize > this.maxFileSize) {
            this.openSnackBar('File too large (' + filesize + 'MB) max file size is ' + this.maxFileSize + 'MB', 'error')
            return;
        }
        this.showCropper = true;
        this.imageChangedEvent = event;

        var reader: FileReader = new FileReader();
        reader.readAsDataURL(this.photoFile);
        reader.onload = (_event): void => {
            if (reader.result) {
                this.image = reader.result;
            } else {
                this.openSnackBar("Reader result returned null.", 'error')
            }
        }
    }

    checkEligibility(): void {
        this.myStepper?.next();
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }

    setAnswers(answers: QuestionAnswer[]): void {
        this.eligibilityAnswers = answers;
    }

    setProfileAnswers(answers: QuestionAnswer[]): void {
        this.myStepper?.next();
        this.profileAnswers = answers;
    }

    setUserRequiresProfiling(requiresProfiling: boolean): void {
        this.userRequiresProfiling = requiresProfiling;
        if (requiresProfiling) {
            //     Add validation
            this.signUpForm.controls['email'].enable();
            this.signUpForm.controls['confirmEmail'].enable();
        } else {
            //     Remove validation
            this.signUpForm.controls['email'].disable();
            this.signUpForm.controls['confirmEmail'].disable();
        }
    }

    finishLoginIfValid(invalid: boolean): void {
        if (!invalid) {
            this.finishLogin();
        }
    }

    setUserEmail(email: string | null): void {
        this.signUpForm.get('email')?.setValue(email);
        this.signUpForm.get('confirmEmail')?.setValue(email);
    }

    showPassword(): void {
        if (this.passwordType == 'password') {
            this.passwordType = 'text';
        } else {
            this.passwordType = 'password';
        }
    }

    clean(obj: any) {
        // remove empty keys
        for (var propName in obj) {
            if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
                delete obj[propName];
            }
        }
        return obj
    }

    validateForm(formGroup: FormGroup | FormArray, parentKey: string = ''): void {
        Object.keys(formGroup.controls).forEach(key => {
            const control = formGroup.get(key);
            const controlPath = parentKey ? `${parentKey}.${key}` : key;

            if (control instanceof FormGroup || control instanceof FormArray) {
                this.validateForm(control, controlPath);  // Recursive call
            } else if (control && control.invalid) {
                console.log(`❌ Invalid field: ${controlPath}`);
                console.log('  🔸 Errors:', control.errors);
            }
        });
    }
}
